// ============================================================================
// ClientMessagesPage — route /me/messages (guarded). The client's 1:1 chat
// thread with their trainer (Wave 5).
// ----------------------------------------------------------------------------
// This page owns the CLIENT realm's data (its own React Query hooks + keys) and
// the mark-read-on-open behaviour, then delegates the whole chat presentation to
// the shared <MessageThread> component (composer + bubble list), parameterized
// by realm='client' so the client's own messages align right.
//   • body rendered as PLAIN TEXT, unicode emoji, image attach ≤2 MB, "Seen"
//     receipt — all in the shared component.
//   • marks the thread read on open (and when new trainer messages arrive).
//   • live-syncs via the client WS (useClientLiveSync, mounted in the shell):
//     a message/read from the trainer updates the thread + badge with no reload.
// ============================================================================
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MessageThread } from "../../components";
import type { MessageThreadHandlers } from "../../components";
import { clientApi } from "../../api";
import type { Message } from "../../api";
import {
  useClientMessages,
  useMarkClientThreadRead,
  useSendClientMessage,
  useSendClientMessageAttachment,
} from "../hooks";

export function ClientMessagesPage() {
  const { t } = useTranslation("messages");

  const thread = useClientMessages();
  const sendMsg = useSendClientMessage();
  const sendAttachment = useSendClientMessageAttachment();
  const markRead = useMarkClientThreadRead();

  const messages = useMemo<Message[]>(
    () => thread.data?.messages ?? [],
    [thread.data],
  );
  const unread = thread.data?.unread ?? 0;

  // Mark the thread read on open and whenever new TRAINER messages arrive (the
  // server only stamps the counterpart's unread, never our own).
  useEffect(() => {
    if (unread > 0 && !markRead.isPending) {
      markRead.mutate();
    }
    // markRead is stable enough; intentionally key on the unread count only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  const handlers: MessageThreadHandlers = {
    send: (body) => sendMsg.mutateAsync({ body }),
    sending: sendMsg.isPending,
    sendAttachment: (file, body) => sendAttachment.mutateAsync({ file, body }),
    attaching: sendAttachment.isPending,
    attachmentUrl: (photoId) => clientApi.messages.attachmentUrl(photoId),
  };

  return (
    <div className="flex h-[76vh] flex-col">
      <h1 className="mb-3 font-display text-2xl font-extrabold tracking-tightest text-body">
        {t("title")}
      </h1>
      <MessageThread
        realm="client"
        messages={messages}
        loading={thread.isLoading}
        errored={thread.isError}
        handlers={handlers}
        className="min-h-0 flex-1"
      />
    </div>
  );
}
